import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api';
import { useSSE } from '../hooks/useSSE';
import type { Fix, Metrics as M, ResponseItem } from '../types';
import { Metrics } from './Metrics';
import { KanbanBoard } from './KanbanBoard';
import { FixesBoard } from './FixesBoard';
import { SubmitForm } from './SubmitForm';
import { AdminPanel } from './AdminPanel';
import { Archive } from './Archive';
import { BottomNav, type Tab } from './BottomNav';

type Props = { currentUser: string; isAdmin: boolean; adminKey?: string; onSwitch: () => void };

export function Dashboard({ currentUser, isAdmin, adminKey, onSwitch }: Props) {
  const [weekKey, setWeekKey] = useState<string>('');
  const [items, setItems] = useState<ResponseItem[]>([]);
  const [fixes, setFixes] = useState<Fix[]>([]);
  const [metrics, setMetrics] = useState<M | null>(null);
  const [tab, setTab] = useState<Tab>('board');
  const [showAdmin, setShowAdmin] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [authError, setAuthError] = useState(false);

  const loadAll = useCallback(async () => {
    if (isAdmin) {
      try {
        const [wk, fx, mt] = await Promise.all([
          api.responses.listWeek(undefined, adminKey),
          api.fixes.list(adminKey),
          api.metrics(),
        ]);
        setWeekKey(wk.weekKey);
        setItems(wk.items);
        setFixes(fx);
        setMetrics(mt);
        setAuthError(false);
      } catch {
        setAuthError(true);
      }
    } else {
      // Member: only ever fetch their own answers.
      const wk = await api.responses.listMine(currentUser);
      setWeekKey(wk.weekKey);
      setItems(wk.items);
      setFixes([]);
      setMetrics(null);
    }
  }, [isAdmin, adminKey, currentUser]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useSSE((event) => {
    if (!isAdmin) {
      if (event === 'response') api.responses.listMine(currentUser).then(r => setItems(r.items)).catch(() => {});
      return;
    }
    if (event === 'response' || event === 'reaction' || event === 'comment') {
      api.responses.listWeek(weekKey, adminKey).then(r => setItems(r.items)).catch(() => {});
      api.metrics().then(setMetrics).catch(() => {});
    }
    if (event === 'fixes') {
      api.fixes.list(adminKey).then(setFixes).catch(() => {});
      api.metrics().then(setMetrics).catch(() => {});
    }
  }, [weekKey, isAdmin, adminKey, currentUser]);

  const myResponse = useMemo(
    () => items.find(r => r.memberName === currentUser),
    [items, currentUser]
  );

  const openFixes = fixes.filter(f => f.status !== 'shipped').length;

  const handleTab = (t: Tab) => {
    if (t === 'more') { setShowMore(true); return; }
    setTab(t);
  };

  // Members: private, submit-only. They never receive anyone else's answers.
  if (!isAdmin) {
    return (
      <div className="min-h-[100dvh] flex flex-col safe-x"
        style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <header className="app-header safe-x">
          <div className="flex items-center gap-3 px-1 py-2">
            <img src="/logo.png" alt="Coastline K9" className="h-7 w-7 shrink-0 object-contain" />
            <div className="min-w-0 flex-1">
              <div className="font-heading text-base leading-none">COASTLINE&nbsp;K9</div>
              <div className="text-[10px] uppercase tracking-widest text-coast-muted mt-0.5 truncate">
                {currentUser} · {weekKey || '…'}
              </div>
            </div>
            <button className="btn btn-ghost text-xs" onClick={onSwitch}>Switch</button>
          </div>
        </header>
        <main className="flex-1 safe-x pt-3 pb-6">
          <SubmitForm
            memberName={currentUser}
            initial={myResponse ? {
              perfect: myResponse.perfect, wrong: myResponse.wrong,
              timeSink: myResponse.timeSink, ask: myResponse.ask,
            } : undefined}
            onSubmitted={() => { loadAll(); }}
          />
          <p className="text-center text-[10px] uppercase tracking-widest text-coast-muted mt-6">
            Private · Goes only to Erick
          </p>
        </main>
      </div>
    );
  }

  // Admin entered the wrong passcode.
  if (isAdmin && authError) {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center text-center px-6">
        <img src="/logo.png" alt="Coastline K9" className="h-12 w-12 object-contain mb-4" />
        <h1 className="font-heading text-2xl mb-2">Passcode incorrect</h1>
        <p className="text-coast-muted text-sm mb-6">That admin passcode didn&apos;t match.</p>
        <button className="btn btn-primary" onClick={onSwitch} style={{ minHeight: 52 }}>Try again</button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col">
      {/* Sticky notch-aware header */}
      <header className="app-header safe-x">
        <div className="flex items-center gap-3 px-1 py-2">
          <img src="/logo.png" alt="Coastline K9" className="h-7 w-7 shrink-0 object-contain" />
          <div className="min-w-0 flex-1">
            <div className="font-heading text-base leading-none">COASTLINE&nbsp;K9</div>
            <div className="text-[10px] uppercase tracking-widest text-coast-muted mt-0.5 truncate">
              {currentUser} · {weekKey || '…'}
            </div>
          </div>
          <div className="flex-1 max-w-[260px]">
            <Metrics metrics={metrics} />
          </div>
        </div>
      </header>

      <main className="flex-1 safe-x pb-nav pt-3">
        {tab === 'submit' && (
          <SubmitForm
            memberName={currentUser}
            initial={myResponse ? {
              perfect: myResponse.perfect,
              wrong:   myResponse.wrong,
              timeSink: myResponse.timeSink,
              ask:     myResponse.ask,
            } : undefined}
            onSubmitted={() => { loadAll(); setTab('board'); }}
          />
        )}

        {tab === 'board' && (
          <motion.section
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} layout
          >
            <div className="flex items-baseline justify-between mb-2 px-1">
              <h2 className="font-heading text-2xl">
                This Week · {weekKey}
              </h2>
              <div className="text-[10px] uppercase tracking-widest text-coast-muted">
                {items.length} in
              </div>
            </div>
            <KanbanBoard items={items} currentUser={currentUser} />
          </motion.section>
        )}

        {tab === 'fixes' && (
          <FixesBoard
            fixes={fixes}
            currentUser={currentUser}
            isAdmin={isAdmin}
            onChange={loadAll}
          />
        )}
      </main>

      <BottomNav
        active={tab === 'more' ? 'more' : tab}
        onChange={handleTab}
        fixesOpenCount={openFixes}
        submitDone={!!myResponse}
      />

      {/* "More" bottom sheet: Archive / Admin / Switch user */}
      <AnimatePresence>
        {showMore && (
          <>
            <motion.div
              className="sheet-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowMore(false)}
            />
            <motion.aside
              className="sheet"
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 32, stiffness: 320 }}
              role="dialog" aria-label="More"
            >
              <div className="sheet-handle" />
              <header className="px-4 py-2 border-b border-coast-hairline flex items-center justify-between">
                <h2 className="font-heading text-2xl">More</h2>
                <button className="btn btn-ghost" onClick={() => setShowMore(false)}>Close</button>
              </header>
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                <MoreItem
                  label="Archive"
                  desc="Browse past weeks · search all responses"
                  onClick={() => { setShowMore(false); setShowArchive(true); }}
                />
                {isAdmin && (
                  <MoreItem
                    label="Admin"
                    desc="Team roster · CSV export · Claude analysis"
                    onClick={() => { setShowMore(false); setShowAdmin(true); }}
                    accent
                  />
                )}
                <MoreItem
                  label="Switch User"
                  desc="Sign out to the name picker"
                  onClick={() => { setShowMore(false); onSwitch(); }}
                />
                <div className="pt-4 text-center text-[10px] uppercase tracking-widest text-coast-muted">
                  Coastline K9 · Live · SSE Connected
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAdmin && (
          <AdminPanel
            onClose={() => setShowAdmin(false)}
            currentUser={currentUser}
            items={items}
            adminKey={adminKey}
          />
        )}
        {showArchive && <Archive onClose={() => setShowArchive(false)} adminKey={adminKey} />}
      </AnimatePresence>
    </div>
  );
}

function MoreItem({
  label, desc, onClick, accent,
}: {
  label: string; desc: string; onClick: () => void; accent?: boolean;
}) {
  return (
    <button
      className={`w-full flex items-center justify-between px-4 py-4
                  border ${accent ? 'border-coast-blue' : 'border-coast-hairline'}
                  bg-coast-card active:border-coast-blue`}
      onClick={onClick}
      style={{ minHeight: 64 }}
    >
      <div className="text-left">
        <div className={`text-base font-heading ${accent ? 'text-coast-blue' : ''}`}>{label}</div>
        <div className="text-[11px] uppercase tracking-widest text-coast-muted">{desc}</div>
      </div>
      <div className="text-coast-blue text-xl leading-none">›</div>
    </button>
  );
}
